// @ts-check
import React, { useCallback, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Button } from '@material-ui/core';
import PublishIcon from '@material-ui/icons/Publish';

import * as FileSaver from 'file-saver';
import XSLX from 'sheetjs-style';

const nextLetter = (s) => {
  return s.replace(/([a-zA-Z])[^a-zA-Z]*$/, function (a) {
    var c = a.charCodeAt(0);
    switch (c) {
      case 90:
        return 'A';
      case 122:
        return 'a';
      default:
        return String.fromCharCode(++c);
    }
  });
};

// Heavy: 16 columns × N rows. Only call this when the user actually clicks
// Export — previously this ran on every render of the parent.
const stringify = (drivers) => {
  const data = [];
  const headers = [
    'ID',
    'Name',
    'Email',
    'Mobile No',
    'Employee Code',
    'Status',
    'Created At',
    'Created By',
    'Blocked',
    'Vehicle Type',
    'Hub IDs',
    'Hubs',
    'Payment Type',
    'Last Active',
    'First Trip',
    'Last Trip'
  ];
  const widths = [];

  for (const driver of drivers) {
    let createdBy = '';
    if (driver.created_by) {
      if (typeof driver.created_by === 'string') {
        createdBy = driver.created_by;
      } else if (driver.created_by?.email) {
        createdBy = driver.created_by.email;
      } else if (driver.created_by?.name) {
        createdBy = driver.created_by.name;
      } else if (
        driver.created_by?._path?.segments &&
        Array.isArray(driver.created_by._path.segments) &&
        driver.created_by._path.segments.length > 0
      ) {
        const segs = driver.created_by._path.segments;
        createdBy = segs[segs.length - 1];
      } else if (driver.created_by?.id) {
        createdBy = driver.created_by.id;
      }
    }

    const hubIds = Array.isArray(driver.hubs)
      ? driver.hubs
          .filter((h) => h?.id)
          .map((h) => h.id)
          .join(', ')
      : '';
    const hubNames = Array.isArray(driver.hubs)
      ? driver.hubs
          .filter((h) => h?.name)
          .map((h) => h.name)
          .join(', ')
      : '';
    const paymentType = driver.payment_setting?.name || driver.wallet_payment_type || '';

    const row = [
      `${driver.id || ''}`,
      `${driver.name || ''}`,
      `${driver.email || ''}`,
      `${driver.mobile_no || ''}`,
      `${driver.employee_code || ''}`,
      `${driver.status || ''}`,
      `${driver.created_at || ''}`,
      `${createdBy}`,
      `${driver.blocked || false}`.toUpperCase(),
      `${driver.vehicle_type || ''}`,
      `${hubIds}`,
      `${hubNames}`,
      `${paymentType}`,
      `${driver.last_login || ''}`,
      `${driver.first_trip || ''}`,
      `${driver.last_trip || ''}`
    ];

    for (let i = 0; i < headers.length; i++) {
      const max = Math.max(row[i].length, headers[i].length, widths[i] || -1, 10);
      const w = Math.min(max, 39); // Limit width to 50 characters
      widths[i] = w;
    }
    data.push(row);
  }
  return { headers, data, widths };
};

const useStyles = makeStyles((theme) => ({
  fab: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginRight: 0,
    marginLeft: 0,
    zIndex: 5,
    width: '100%'
  },
  margin: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1)
  }
}));

const ExportActionButtonSize = ({ excelData, fileName }) => {
  const classes = useStyles();
  const [exporting, setExporting] = useState(false);

  const fileType =
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=utf-8';

  const exportToExcel = useCallback(async () => {
    if (exporting) return;
    setExporting(true);
    try {
      // Heavy work happens here — on click, not on render — so mounting the
      // button is cheap even for 5k+ drivers.
      const { headers, data, widths } = stringify(excelData);

      const ws = XSLX.utils.book_new();
      XSLX.utils.sheet_add_aoa(ws, [headers]);
      XSLX.utils.sheet_add_json(ws, data, { origin: 'A2', skipHeader: true });
      const wb = { Sheets: { data: ws }, SheetNames: ['data'] };
      const column_widths = widths.map((w) => ({ width: w + 1 }));
      const style = { alignment: { wrapText: true } };

      let letter = 'A';
      for (let i = 0; i < headers.length; i++) {
        for (let j = 2; j <= data.length; j++) {
          const cell = letter + j;
          if (ws[cell]) ws[cell].s = style;
        }
        if (ws[letter + '1']) {
          ws[letter + '1'].s = { font: { bold: true } };
        }
        letter = nextLetter(letter);
      }

      ws['!cols'] = column_widths;

      const excelBuffer = XSLX.write(wb, {
        bookType: 'xlsx',
        type: 'array',
        cellStyles: true
      });
      const finalData = new Blob([excelBuffer], { type: fileType });
      FileSaver.saveAs(finalData, fileName + '.xlsx');
    } finally {
      setExporting(false);
    }
  }, [excelData, fileName, exporting]);

  return (
    <div className={classes.fab}>
      <div>
        <Button
          className={classes.margin}
          type="submit"
          variant="contained"
          color="primary"
          fullWidth={true}
          disabled={exporting || !excelData || excelData.length === 0}
          onClick={exportToExcel}
        >
          <PublishIcon /> {exporting ? 'Exporting…' : 'Export'}
        </Button>
      </div>
    </div>
  );
};

export default ExportActionButtonSize;
